import React, { useEffect, useState, useRef } from 'react';
import { api, Post, Tip, User } from '../lib/api';
import { ContentCard } from '../components/ContentCard';
import { TierBadge } from '../components/TierBadge';

interface DashboardProps {
  user: User | null;
  onUserUpdate: (user: User) => void;
}

export const Dashboard: React.FC<DashboardProps> = ({ user, onUserUpdate }) => {
  // If not logged in, show connect wallet prompt
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [posts, setPosts] = useState<Post[]>([]);
  const [tips, setTips] = useState<Tip[]>([]);
  const [earnings, setEarnings] = useState<{ total: number; count: number }>({ total: 0, count: 0 });
  
  // Become Creator Form
  const [tipWallet, setTipWallet] = useState<string>('');

  // Upload Post Form
  const [caption, setCaption] = useState<string>('');
  const [tier, setTier] = useState<string>('free');
  const [file, setFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Edit Profile Form
  const [displayName, setDisplayName] = useState<string>('');
  const [bio, setBio] = useState<string>('');
  const [avatarUrl, setAvatarUrl] = useState<string>('');
  const [updatingProfile, setUpdatingProfile] = useState<boolean>(false);

  const fetchDashboardData = async () => {
    if (!user || user.is_creator !== 1) return;
    try {
      setLoading(true);
      setError(null);

      // Load creator's own posts from global feed
      const postsRes = await api.getPosts(1, 50);
      const myPosts = postsRes.posts.filter((p) => p.creator_id === user.id);
      setPosts(myPosts);

      // Load tips received
      const tipsRes = await api.getCreatorTips(user.id);
      setTips(tipsRes.tips);
      setEarnings({
        total: tipsRes.total_received,
        count: tipsRes.total_count
      });

      // Populate profile forms
      setDisplayName(user.display_name || '');
      setBio(user.bio || '');
      setAvatarUrl(user.avatar_key || '');
    } catch (err: any) {
      console.error('Failed to load dashboard data', err);
      setError(err.message || 'Failed to load dashboard.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    if (user) {
      setTipWallet(user.tip_wallet || user.wallet);
    }
  }, [user]);

  const handleBecomeCreator = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      const res = await api.applyCreator(tipWallet.trim() || undefined);
      onUserUpdate(res.user);
    } catch (err: any) {
      console.error('Failed to become creator', err);
      setError(err.message || 'Failed to apply.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setUpdatingProfile(true);
      setError(null);
      const res = await api.updateProfile({
        display_name: displayName.trim() || undefined,
        bio: bio.trim() || undefined,
        avatar_key: avatarUrl.trim() || undefined,
      });
      onUserUpdate(res.user);
      alert('Profile updated successfully!');
    } catch (err: any) {
      console.error('Failed to update profile', err);
      setError(err.message || 'Failed to update profile.');
    } finally {
      setUpdatingProfile(false);
    }
  };

  const handleCreatePost = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      alert('Please select a photo or video to upload.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setUploadProgress('Uploading file to R2...');

      // 1. Upload media key
      const uploadRes = await api.uploadMedia(file);
      
      setUploadProgress('Creating post record...');

      // 2. Create post
      await api.createPost({
        caption: caption.trim() || undefined,
        media_key: uploadRes.key,
        tier
      });

      // Clear form
      setCaption('');
      setTier('free');
      setFile(null);
      setUploadProgress('');
      if (fileInputRef.current) fileInputRef.current.value = '';

      // Reload posts
      fetchDashboardData();
      alert('Post published successfully! 🦶🔥');
    } catch (err: any) {
      console.error('Failed to create post', err);
      setError(err.message || 'Failed to publish post.');
      setUploadProgress('');
    } finally {
      setLoading(false);
    }
  };

  const handlePostDeleted = (deletedId: string) => {
    setPosts(prev => prev.filter(p => p.id !== deletedId));
  };

  if (!user) {
    return (
      <div className="glass-card" style={{ textAlign: 'center', padding: '4rem 2rem', marginTop: '2rem' }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🦶🔌</div>
        <h3>Wallet Required</h3>
        <p className="text-secondary" style={{ marginTop: '0.5rem' }}>
          Please connect your wallet to access the creator dashboard.
        </p>
      </div>
    );
  }

  // Render application form if user is not a creator yet
  if (user.is_creator === 0) {
    return (
      <div style={{ maxWidth: '600px', margin: '2rem auto' }}>
        <div className="glass-card" style={{ padding: '2.5rem' }}>
          <h2 style={{ fontSize: '1.75rem', marginBottom: '0.5rem', fontFamily: 'var(--font-heading)' }}>
            Become a Sole Creator 💎🦶
          </h2>
          <p className="text-secondary" style={{ fontSize: '0.95rem', marginBottom: '1.5rem' }}>
            Set up your tip wallet and start posting exclusive content to earn $SOLEM8 tokens.
          </p>

          {error && (
            <div style={{ 
              background: 'rgba(239, 68, 68, 0.1)', 
              border: '1px solid rgba(239, 68, 68, 0.2)', 
              color: '#ef4444', 
              padding: '0.8rem 1rem', 
              borderRadius: 'var(--radius-md)', 
              fontSize: '0.85rem', 
              marginBottom: '1.25rem' 
            }}>
              ⚠️ {error}
            </div>
          )}

          <form onSubmit={handleBecomeCreator} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div className="form-group">
              <label className="form-label">Tipping SPL Token Account / Solana Wallet</label>
              <input 
                type="text" 
                className="form-input" 
                value={tipWallet}
                onChange={(e) => setTipWallet(e.target.value)}
                placeholder="Enter Solana wallet address for tips"
                required
              />
              <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                Defaults to your current login wallet. Tips in $SOLEM8 will go directly to this address.
              </span>
            </div>

            <button type="submit" className="btn btn-primary" style={{ marginTop: '1rem' }} disabled={loading}>
              {loading ? 'Activating Profile...' : 'Activate Creator Account 🦶✨'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '3rem' }}>
      
      {/* 1. DASHBOARD OVERVIEW STATS */}
      <section className="glass-card" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', textAlign: 'center', padding: '1.5rem' }}>
        <div>
          <h4 className="text-muted" style={{ fontSize: '0.75rem', textTransform: 'uppercase', marginBottom: '0.25rem' }}>Total Earnings</h4>
          <h2 className="gradient-text" style={{ fontSize: '2rem' }}>{earnings.total}</h2>
          <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>$SOLEM8 tokens</p>
        </div>
        <div style={{ borderLeft: '1px solid var(--border-glass)', borderRight: '1px solid var(--border-glass)' }}>
          <h4 className="text-muted" style={{ fontSize: '0.75rem', textTransform: 'uppercase', marginBottom: '0.25rem' }}>Tips Count</h4>
          <h2 className="gradient-text" style={{ fontSize: '2rem' }}>{earnings.count}</h2>
          <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>transactions</p>
        </div>
        <div>
          <h4 className="text-muted" style={{ fontSize: '0.75rem', textTransform: 'uppercase', marginBottom: '0.25rem' }}>My Posts</h4>
          <h2 className="gradient-text" style={{ fontSize: '2rem' }}>{posts.length}</h2>
          <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>exclusive pieces</p>
        </div>
      </section>

      {/* 2. MAIN HUB: UPLOAD & EDIT PROFILE */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '2rem' }}>
        
        {/* Publish Content */}
        <div className="glass-card">
          <h3 style={{ fontSize: '1.25rem', marginBottom: '1.25rem', fontFamily: 'var(--font-heading)' }}>
            Publish Exclusive Content 🦶📸
          </h3>
          
          {uploadProgress && (
            <div style={{ 
              background: 'rgba(139, 92, 246, 0.1)', 
              border: '1px solid rgba(139, 92, 246, 0.2)', 
              color: 'var(--accent-secondary)', 
              padding: '0.8rem 1rem', 
              borderRadius: 'var(--radius-md)', 
              fontSize: '0.85rem', 
              marginBottom: '1.25rem'
            }} className="shimmer">
              ⚡ {uploadProgress}
            </div>
          )}

          <form onSubmit={handleCreatePost} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div className="form-group">
              <label className="form-label">Upload Photo/Video</label>
              <input 
                type="file" 
                ref={fileInputRef}
                accept="image/*,video/*"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                style={{
                  background: 'rgba(0, 0, 0, 0.2)',
                  border: '1px dashed var(--border-glass)',
                  padding: '1.5rem',
                  borderRadius: 'var(--radius-md)',
                  cursor: 'pointer',
                  width: '100%',
                  color: 'var(--text-secondary)'
                }}
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Caption / Description</label>
              <textarea 
                className="form-input" 
                placeholder="Write something tempting about your soles..."
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                rows={3}
                style={{ resize: 'none' }}
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Lock Gating Tier</label>
              <select 
                className="form-select" 
                value={tier}
                onChange={(e) => setTier(e.target.value)}
                disabled={loading}
              >
                <option value="free">Free (Anyone can view) 👣</option>
                <option value="bronze">Bronze (Hold &ge; 1,000 $SOLEM8) 🥉</option>
                <option value="silver">Silver (Hold &ge; 10,000 $SOLEM8) 🥈</option>
                <option value="gold">Gold (Hold &ge; 100,000 $SOLEM8) 🥇</option>
                <option value="diamond">Diamond (Hold &ge; 1,000,000 $SOLEM8) 💎</option>
              </select>
            </div>

            <button type="submit" className="btn btn-primary" style={{ marginTop: '0.5rem' }} disabled={loading}>
              {loading ? 'Publishing...' : 'Publish Exclusive Post 🦶🔥'}
            </button>
          </form>
        </div>

        {/* Edit Profile */}
        <div className="glass-card">
          <h3 style={{ fontSize: '1.25rem', marginBottom: '1.25rem', fontFamily: 'var(--font-heading)' }}>
            Edit Creator Profile 📝
          </h3>
          <form onSubmit={handleUpdateProfile} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div className="form-group">
              <label className="form-label">Display Name</label>
              <input 
                type="text" 
                className="form-input" 
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="e.g. Princess Arch"
                required
                disabled={updatingProfile}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Creator Bio</label>
              <textarea 
                className="form-input" 
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                placeholder="Welcome holders! Custom sole requests are open."
                rows={3}
                style={{ resize: 'none' }}
                disabled={updatingProfile}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Avatar Key / Key URL</label>
              <input 
                type="text" 
                className="form-input" 
                value={avatarUrl}
                onChange={(e) => setAvatarUrl(e.target.value)}
                placeholder="Paste key url or R2 link"
                disabled={updatingProfile}
              />
            </div>

            <button type="submit" className="btn btn-secondary" style={{ marginTop: '0.5rem' }} disabled={updatingProfile}>
              {updatingProfile ? 'Saving Changes...' : 'Save Settings 💾'}
            </button>
          </form>
        </div>
      </div>

      {/* 3. MY EXCLUSIVE POSTS */}
      <section style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        <h3 style={{ fontSize: '1.5rem', fontFamily: 'var(--font-heading)' }}>My Posts 📸</h3>
        {posts.length === 0 ? (
          <div className="glass-card" style={{ textAlign: 'center', padding: '3rem' }}>
            <p className="text-muted">You haven't posted any exclusive content yet.</p>
          </div>
        ) : (
          <div className="explore-grid">
            {posts.map((post) => (
              <ContentCard 
                key={post.id} 
                post={post} 
                currentUserId={user.id}
                onDelete={handlePostDeleted}
              />
            ))}
          </div>
        )}
      </section>

      {/* 4. RECENT TRANSACTION TIPS */}
      {tips.length > 0 && (
        <section style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <h3 style={{ fontSize: '1.5rem', fontFamily: 'var(--font-heading)' }}>Recent Income Tips 💸</h3>
          <div className="glass-card" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {tips.map((tip) => (
              <div 
                key={tip.id} 
                style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  borderBottom: '1px solid var(--border-glass)',
                  paddingBottom: '0.75rem'
                }}
              >
                <div>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>
                    From @{tip.from_wallet.slice(0, 4)}...{tip.from_wallet.slice(-4)}
                  </div>
                  {tip.message && (
                    <p className="text-secondary" style={{ fontSize: '0.8rem', marginTop: '0.2rem', fontStyle: 'italic' }}>
                      "{tip.message}"
                    </p>
                  )}
                  <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                    Signature: {tip.tx_sig.slice(0, 10)}...
                  </span>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: 800, color: 'var(--accent-primary)', fontSize: '1rem' }}>
                    +{tip.amount_tokens} $SOLEM8
                  </div>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                    {new Date(tip.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

    </div>
  );
};
